import ListLoader from './ListLoader.js';
import { uniqueValues, parseOrderList, valueCount } from './listFacets.js';

/**
 * ListView
 * 一覧の中核。年別JSONを結合（ListLoader）→絞り込み(applyFilters)→ソート→ページ分割→描画＋ページネーション。
 * - filterState を保持し setFilter() で更新→再描画（Phase2の絞り込みUIから呼ぶ）。Phase1はUI未配線＝全件。
 * - 絞り込み・ページはURLクエリ（?page=/?q=/facet）と同期（パラメータ型）。読込時にURLから復元。
 * - 描画は c-card-blog（type別にテンプレ差し替え可能）＋ページネーションは c-pagination 構造を生成。
 *
 * マークアップ（data属性で設定）：
 *   <div class="l-archive-list js-list"
 *        data-list-type="blog" data-list-base="/blog/"
 *        data-per-page="12" data-facets="category,tags" data-enable-search="false"></div>
 *   件数：同じ .l-archive-main 内の [data-list-count]
 *   ページャ：同じ .l-archive-main 内の [data-list-pagination]
 *   canonical対象facet：data-list-canonical（既定"category"。"category,tags"等で拡張）。
 *     対象facetがURLにあれば <link rel="canonical"> を self（base+?facet=値）に、無ければ base に戻す。
 *
 * 設計：.claude/docs/forcle_design/coding/15_list_system.md
 */
export default class ListView {
  constructor(container) {
    this.el = container;
    this.type = container.dataset.listType || '';
    this.basePath = container.dataset.listBase || '/';
    this.perPage = parseInt(container.dataset.perPage, 10) || 12;
    this.facetFields = (container.dataset.facets || '').split(',').map((s) => s.trim()).filter(Boolean);
    this.enableSearch = container.dataset.enableSearch === 'true';
    // canonical に含める facet（既定=category。data-list-canonical="category,tags" で上書き）。
    // カテゴリ絞り込みビューを self-canonical にしてカテゴリ別にindexさせる。将来タグ等を足す時はここ/属性に追加するだけ
    this.canonicalFacets = (container.dataset.listCanonical || 'category').split(',').map((s) => s.trim()).filter(Boolean);
    this.canonicalLink = document.querySelector('link[rel="canonical"]');
    this.baseCanonical = this.canonicalLink ? this.canonicalLink.href.split('?')[0] : '';

    const scope = container.closest('.l-archive-main') || document;
    this.countEls = scope.querySelectorAll('[data-list-count]');
    this.rangeEl = scope.querySelector('[data-list-range]');
    this.pagerEl = scope.querySelector('[data-list-pagination]');
    // 見出し（例 <h2 data-list-heading="ブログ">）：カテゴリ選択で「〇〇のブログ」／未選択「すべてのブログ」に更新
    this.headingEl = scope.querySelector('[data-list-heading]');

    this.all = [];
    this.filterState = { keyword: '', facets: {} };
    this.currentPage = 1;
    // 全年のJSONが揃うまでは true にしない。揃うまでの件数表示はMTが埋め込んだ総件数を優先する
    this.isComplete = false;

    this.init();
  }

  async init() {
    this.restoreFromUrl();
    this.hidePickupOnFilteredLoad();
    this.el.classList.add('is-list-loading');
    try {
      // 年別JSONは1本でも応答が遅いと全体が待たされるため、新しい年から届いた分で先に描画する
      this.all = await ListLoader.load({
        basePath: this.basePath,
        type: this.type,
        onPartial: (items) => {
          this.applyData(items);
          this.buildFilters();
          this.render();
        }
      });
      this.applyData(this.all);
    } catch (e) {
      this.all = [];
    }
    this.isComplete = true;
    this.buildFilters();
    this.render();

    // ブラウザの戻る/進むでURL状態に追従
    window.addEventListener('popstate', () => {
      this.restoreFromUrl();
      this.render();
    });
  }

  // 取得済みitemsを一覧の表示順（公開日の降順）に整える。段階描画で複数回呼ばれる
  applyData(items) {
    this.all = items;

    if (this.type === 'seminar') {
      this.all.forEach((item) => this.computeStatus(item));
    }

    this.all.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }

  /* ===== 絞り込みUI（パラメータ型・データから動的生成。blog/news/seminar/works 共通エンジン） =====
   * 対応UI（いずれも .l-archive 内を探索）：
   *  - ピル `[data-list-filter="<field>"]`：お知らせ＝単一1個／セミナー・事例＝search-tableの複数行。
   *    複数選択は `data-list-filter-multi`、ピルのクラスは `data-list-filter-pill`（既定 c-filter-item）、
   *    ラベル接頭辞は `data-list-filter-prefix`（事例タグの "# " 等）。
   *  - サイドバー カテゴリ `[data-sidebar-cats="<field>"]`（ノウハウ）＝単一選択＋件数
   *  - サイドバー タグ `[data-sidebar-tags="<field>"]`（ノウハウ）＝複数選択
   *  - キーワード入力 `[data-list-keyword]`（enableSearch時）＋ 適用ボタン `[data-list-search]`
   */
  buildFilters() {
    const scope = this.el.closest('.l-archive') || document;
    this.pillEls = Array.from(scope.querySelectorAll('[data-list-filter]'));
    this.catsEl = scope.querySelector('[data-sidebar-cats]');
    this.tagsEl = scope.querySelector('[data-sidebar-tags]');
    this.keywordEl = scope.querySelector('[data-list-keyword]');

    this.pillEls.forEach((el) => this.buildPills(el));
    if (this.catsEl) this.buildCats(this.catsEl);
    if (this.tagsEl) this.buildTags(this.tagsEl);
    if (this.enableSearch && this.keywordEl) this.buildKeyword(this.keywordEl, scope);

    this.updateFilterActive();
  }

  // ピル（単一＝先頭に「すべて」／複数＝トグル）。お知らせ＝c-filter-item・1行／セミナー事例＝p-search-table-pill・複数行
  buildPills(el) {
    const field = el.dataset.listFilter;
    const multi = el.dataset.listFilterMulti !== undefined;
    const cls = el.dataset.listFilterPill || 'c-filter-item';
    const prefix = el.dataset.listFilterPrefix || '';
    // 複数選択（＝タグ系）は五十音・ABC順に統一（サイドバー タグ一覧・詳細ページと並びを揃える）。単一（カテゴリ）は出現順
    // data-list-filter-order で先頭固定する値を指定可（記載順→残りは ja順）
    const priority = parseOrderList(el.dataset.listFilterOrder);
    const values = uniqueValues(this.all, field, { sort: multi, priority });
    const pill = (val, label) => `<button type="button" class="${cls}" data-value="${this.attr(val)}">${this.escape(label)}</button>`;
    const head = multi ? [] : [pill('', 'すべて')];
    el.innerHTML = head.concat(values.map((v) => pill(v, prefix + v))).join('');
    el.querySelectorAll(`.${cls}`).forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = btn.dataset.value;
        if (multi) {
          const cur = this.filterState.facets[field] || [];
          const next = cur.includes(v) ? cur.filter((x) => x !== v) : cur.concat(v);
          this.setFilter({ facets: { [field]: next } });
        } else {
          this.setFilter({ facets: { [field]: v ? [v] : [] } });
        }
      });
    });
  }

  // サイドバー カテゴリ（単一選択＋件数）。JSONから「すべて(総数)」＋各カテゴリ(件数)を生成。クリックで ?category= 絞り込み
  buildCats(el) {
    const field = el.dataset.sidebarCats;
    const values = uniqueValues(this.all, field, { sort: true });
    const item = (val, label, count) => `<a href="${this.facetHref(field, val)}" data-value="${this.attr(val)}"><span class="label">${this.escape(label)}</span><span class="count">(${count})</span></a>`;
    el.innerHTML = item('', 'すべて', this.all.length)
      + values.map((v) => item(v, v, valueCount(this.all, field, v))).join('');
    el.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const v = a.dataset.value;
        this.setFilter({ facets: { [field]: v ? [v] : [] } });
      });
    });
  }

  // キーワード（即時反映＋適用ボタン）。enableSearch時のみ配線
  buildKeyword(input, scope) {
    const apply = () => this.setFilter({ keyword: input.value.trim() });
    input.addEventListener('input', apply);
    const btn = scope.querySelector('[data-list-search]');
    if (btn) btn.addEventListener('click', apply);
  }

  // サイドバー タグ（複数選択）。ユニーク値の抽出は共通ヘルパ（listFacets）を使用
  buildTags(el) {
    const field = el.dataset.sidebarTags;
    const priority = parseOrderList(el.dataset.sidebarTagsOrder);
    const values = uniqueValues(this.all, field, { sort: true, priority });
    el.innerHTML = values.map((v) => `<a href="${this.facetHref(field, v)}" data-value="${this.attr(v)}">${this.escape(v)}</a>`).join('');
    el.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const cur = this.filterState.facets[field] || [];
        const v = a.dataset.value;
        const next = cur.includes(v) ? cur.filter((x) => x !== v) : cur.concat(v);
        this.setFilter({ facets: { [field]: next } });
      });
    });
  }

  facetHref(field, val) {
    if (!val) return `${window.location.pathname}#list`;
    return `?${encodeURIComponent(field)}=${encodeURIComponent(val)}#list`;
  }

  attr(val) {
    return String(val == null ? '' : val).replace(/"/g, '&quot;');
  }

  updateFilterActive() {
    // ピル＝単一（[0]）／タグ・複数ピル＝複数
    (this.pillEls || []).forEach((el) => {
      const field = el.dataset.listFilter;
      const multi = el.dataset.listFilterMulti !== undefined;
      const cls = el.dataset.listFilterPill || 'c-filter-item';
      const sel = this.filterState.facets[field] || [];
      el.querySelectorAll(`.${cls}`).forEach((b) => {
        const v = b.dataset.value || '';
        b.classList.toggle('is-active', multi ? sel.includes(v) : (sel[0] || '') === v);
      });
    });
    if (this.keywordEl) this.keywordEl.value = this.enableSearch ? (this.filterState.keyword || '') : '';
    if (this.catsEl) {
      // カテゴリ＝単一。選択中カテゴリ（無ければ「すべて」＝空値）を is-active に
      const sel = (this.filterState.facets[this.catsEl.dataset.sidebarCats] || [])[0] || '';
      this.catsEl.querySelectorAll('a').forEach((a) => a.classList.toggle('is-active', (a.dataset.value || '') === sel));
    }
    if (this.tagsEl) {
      const sel = this.filterState.facets[this.tagsEl.dataset.sidebarTags] || [];
      this.tagsEl.querySelectorAll('a').forEach((b) => b.classList.toggle('is-active', sel.includes(b.dataset.value)));
    }
  }

  /* ===== 外部API（Phase2の絞り込みUIから呼ぶ） ===== */
  setFilter(partial = {}) {
    this.filterState = {
      keyword: partial.keyword !== undefined ? partial.keyword : this.filterState.keyword,
      facets: { ...this.filterState.facets, ...(partial.facets || {}) }
    };
    this.currentPage = 1;
    this.syncUrl();
    this.render();
    const anchor = document.getElementById('list') || this.el.closest('.l-archive') || this.el;
    const top = anchor.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  setPage(n) {
    this.currentPage = n;
    this.syncUrl();
    this.render();
    const anchor = this.el.closest('.l-archive') || this.el;
    const top = anchor.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  // 絞り込み（キーワード／ファセット）が1つでも効いているか
  hasActiveFilters() {
    const { keyword, facets } = this.filterState;
    if (this.enableSearch && keyword) return true;
    return Object.values(facets).some((values) => values && values.length);
  }

  /* ===== パイプライン ===== */
  applyFilters(items) {
    const { keyword, facets } = this.filterState;
    return items.filter((item) => {
      // facets：facet間=AND／facet内の複数値=OR。空facetは素通り
      for (const [field, values] of Object.entries(facets)) {
        if (!values || !values.length) continue;
        const iv = item[field];
        const arr = Array.isArray(iv) ? iv : [iv];
        if (!values.some((v) => arr.includes(v))) return false;
      }
      // keyword：enableSearch時のみ。タイトル＋抜粋の部分一致
      if (this.enableSearch && keyword) {
        const hay = `${item.title || ''} ${item.description || ''}`.toLowerCase();
        if (!hay.includes(keyword.toLowerCase())) return false;
      }
      return true;
    });
  }

  render() {
    const filtered = this.applyFilters(this.all);
    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / this.perPage));
    if (this.currentPage > totalPages) this.currentPage = 1;

    const start = (this.currentPage - 1) * this.perPage;
    const pageItems = filtered.slice(start, start + this.perPage);

    // 読み込み途中で件数が増えていくのを見せないよう、全年が揃うまではMT埋め込みの総件数を残す。
    // 絞り込み中は実データの件数が正しいので上書きする
    if (this.isComplete || this.hasActiveFilters()) {
      this.countEls.forEach((el) => { el.textContent = String(total); });
    }
    // 表示レンジ（「○件中1〜12件目」）。現在ページの from〜to
    if (this.rangeEl) {
      const from = total === 0 ? 0 : start + 1;
      const to = start + pageItems.length;
      this.rangeEl.textContent = total === 0 ? '0' : `${from}〜${to}`;
    }

    this.el.innerHTML = total === 0
      ? '<p class="l-archive-empty">該当する記事がありません。</p>'
      : pageItems.map((it) => this.itemHTML(it)).join('');

    if (this.pagerEl) {
      this.pagerEl.innerHTML = totalPages > 1 ? this.paginationHTML(totalPages) : '';
      this.bindPager();
    }

    this.updateFilterActive();
    this.updateCanonical();
    this.updateHeading();

    // 一覧描画完了の合図（FOUC抑止のフェードイン等に使える）
    this.el.classList.remove('is-list-loading');
    this.el.classList.add('is-list-ready');
  }

  /* ===== テンプレート（type別に差し替え） ===== */
  itemHTML(it) {
    // type別にカード/行を出し分け。未定義type＝blogカード（c-card-blog）にフォールバック
    switch (this.type) {
      case 'news': return this.newsRowHTML(it);
      case 'seminar': return this.seminarCardHTML(it);
      case 'works': return this.caseCardHTML(it);
      default: return this.cardHTML(it);
    }
  }

  cardHTML(it) {
    const tags = Array.isArray(it.tags) ? it.tags : [];
    return `<a class="c-card-blog" href="${this.escape(it.url || '#')}">`
      + `<figure class="c-card-blog-thumb"><img src="${this.escape(it.image || '')}" width="373" height="233" alt="" loading="lazy"></figure>`
      + '<div class="c-card-blog-meta">'
      + `<span class="date">${this.formatDate(it.date)}</span>`
      + this.categoryBadges(it.category)
      + '</div>'
      + `<p class="c-card-blog-title">${this.escape(it.title || '')}</p>`
      + (tags.length ? `<ul class="c-card-blog-tags">${tags.map((t) => `<li><span class="c-badge cl-line">${this.escape(t)}</span></li>`).join('')}</ul>` : '')
      + '</a>';
  }

  // セミナーカード（c-card-seminar）。サムネ＋日付/カテゴリ＋タイトル＋開催情報＋下部タグ＋ステータス（カード全体リンク）
  seminarCardHTML(it) {
    const tags = Array.isArray(it.tags) ? it.tags : [];
    const row = (label, val) => (val ? `<li><span class="key">${label}</span><span class="val">${this.escape(val)}</span></li>` : '');
    const attrs = row('開催日', this.formatEventDate(it.eventDate, it.seminarEnd)) + row('開催形式', it.format) + row('定員', it.capacity);
    // ステータスの配色は文言ではなく statusState で決める（文言変更で崩れないように）。既定（closed）＝グレー
    const statusClass = { open: ' is-open', archive: ' is-active' }[it.statusState] || '';
    return `<a class="c-card-seminar" href="${this.escape(it.url || '#')}">`
      + `<figure class="thumb"><img src="${this.escape(it.image || '')}" width="373" height="210" alt="" loading="lazy"></figure>`
      + '<div class="meta">'
      + `<span class="date">${this.formatDate(it.date)}</span>`
      + (it.category ? `<span class="c-badge">${this.escape(it.category)}</span>` : '')
      + '</div>'
      + `<p class="title">${this.escape(it.title || '')}</p>`
      + (attrs ? `<ul class="p-case-pickup-attrs sz-sm">${attrs}</ul>` : '')
      + (tags.length ? `<div class="tags">${tags.map((t) => `<span class="c-badge cl-line">${this.escape(t)}</span>`).join('')}</div>` : '')
      + (it.status ? `<span class="status${statusClass}">${this.escape(it.status)}</span>` : '')
      + '</a>';
  }

  /**
   * セミナーのステータスを開催日・アーカイブ期間から自動計算する
   * @param {Object} item - JSONの1アイテム
   */
    computeStatus(item) {
      const today = new Date();

      const parseDate = (str) => {
        if (!str || !str.trim()) return null;
        const m = str.match(/(\d{4})年(\d{1,2})月\s*(\d{1,2})日(?:\s+(\d{1,2}):(\d{2}))?/);
        if (m) {
          const iso = m[1] + '-' + m[2].padStart(2,'0') + '-' + m[3].padStart(2,'0');
          if (m[4] && m[5]) {
            return new Date(iso + 'T' + m[4].padStart(2,'0') + ':' + m[5]);
          }
          return new Date(iso);
        }
        const d = new Date(str.replace(/\//g, '-'));
        return isNaN(d.getTime()) ? null : d;
      };

      // 時刻のみ（"16:15"）をeventDateの日付と組み合わせてパース
      const parseTimeOnly = (str, baseDate) => {
        if (!str || !baseDate) return null;
        const t = str.match(/^(\d{1,2}):(\d{2})$/);
        if (!t) return null;
        var d = new Date(baseDate);
        d.setHours(parseInt(t[1], 10), parseInt(t[2], 10), 0, 0);
        return d;
      };

      const eventDate = parseDate(item.eventDate);
      const seminarEnd = parseDate(item.seminarEnd) || parseTimeOnly(item.seminarEnd, eventDate);
      const archiveStart = parseDate(item.archiveStart);
      const archiveEnd = parseDate(item.archiveEnd);

      const cutoff = seminarEnd || eventDate;

      if (archiveEnd && today > archiveEnd) {
        item.status = '配信終了';
        item.statusState = 'closed';
      } else if (archiveStart && archiveEnd && today >= archiveStart && today <= archiveEnd) {
        item.status = 'アーカイブ配信中';
        item.statusState = 'archive';
      } else if (cutoff && today <= cutoff) {
        item.status = '受付中';
        item.statusState = 'open';
      } else {
        item.status = '開催終了';
        item.statusState = 'closed';
      }
    }

    // セミナー開催日
    formatEventDate(eventDate, seminarEnd) {
      if (!eventDate) return '';
      const m = eventDate.match(/(\d{4})年(\d{1,2})月\s*(\d{1,2})日(?:\s+(\d{1,2}):(\d{2}))?/);
      if (!m) return eventDate;
      const d = new Date(m[1] + '-' + m[2].padStart(2,'0') + '-' + m[3].padStart(2,'0'));
      const weekdays = ['日','月','火','水','木','金','土'];
      const day = weekdays[d.getDay()];
      const time = (m[4] && m[5]) ? ' ' + m[4] + ':' + m[5] : '';
      const end = seminarEnd ? '-' + seminarEnd : '';
      return m[1] + '年' + parseInt(m[2],10) + '月' + m[3].trim() + '日（' + day + '）' + time + end;
    }

  // 事例カード（c-card-case）。サムネ（ロゴバッジ）＋タイトル＋会社名/カテゴリ＋成果/業種/課題＋タグ（カード全体リンク）。Figma 851
  caseCardHTML(it) {
    const row = (label, val) => (val ? `<li><span class="key">${label}</span><span class="val">${this.escape(val)}</span></li>` : '');
    const logo = it.logo || 'https://placehold.jp/ffffff/999999/200x60.png?text=LOGO';
    const tags = Array.isArray(it.tags) ? it.tags : [];
    return `<a class="c-card-case" href="${this.escape(it.url || '#')}">`
      + '<figure class="thumb">'
      + `<img src="${this.escape(it.image || '')}" width="373" height="233" alt="" loading="lazy">`
      + `<span class="logo"><img src="${this.escape(logo)}" alt="" loading="lazy"></span>`
      + '</figure>'
      + `<p class="title">${this.escape(it.title || '')}</p>`
      + '<div class="meta">'
      + (it.company ? `<p class="company">${this.escape(it.company)} 様</p>` : '')
      + (it.category ? `<span class="c-badge">${this.escape(it.category)}</span>` : '')
      + '</div>'
      + '<ul class="p-case-pickup-attrs">'
      + row('成果', it.result)
      + row('業種', it.industry)
      + row('課題', it.issue)
      + '</ul>'
      + (tags.length ? `<ul class="tags">${tags.map((t) => `<li><span class="c-badge cl-line">${this.escape(t)}</span></li>`).join('')}</ul>` : '')
      + '</a>';
  }

  // ニュース1行（master_news）。日付＋枠線バッジ＋タイトル＋丸矢印（行全体リンク）
  newsRowHTML(it) {
    return `<a class="p-newslist-item" href="${this.escape(it.url || '#')}">`
      + '<div class="p-newslist-item-body">'
      + '<div class="p-newslist-item-data">'
      + `<span class="date">${this.formatDate(it.date)}</span>`
      + (it.category ? `<span class="c-badge cl-line">${this.escape(it.category)}</span>` : '')
      + '</div>'
      + `<p class="p-newslist-item-title">${this.escape(it.title || '')}</p>`
      + '</div>'
      + '<span class="c-btn-icon" aria-hidden="true"></span>'
      + '</a>';
  }

  paginationHTML(totalPages) {
    const cur = this.currentPage;
    const prevOff = cur <= 1;
    const nextOff = cur >= totalPages;

    const nums = this.pageNumbers(cur, totalPages).map((p) => (
      p === '...'
        ? '<span class="ellipsis" aria-hidden="true">…</span>'
        : `<a class="page${p === cur ? ' is-current' : ''}" href="#" data-page="${p}"${p === cur ? ' aria-current="page"' : ''}>${p}</a>`
    )).join('');

    const prev = prevOff
      ? '<span class="arrow arrow--prev is-disabled" aria-hidden="true"></span>'
      : `<a class="arrow arrow--prev" href="#" data-page="${cur - 1}" aria-label="前のページへ"></a>`;
    const next = nextOff
      ? '<span class="arrow arrow--next is-disabled" aria-hidden="true"></span>'
      : `<a class="arrow arrow--next" href="#" data-page="${cur + 1}" aria-label="次のページへ"></a>`;

    return `<nav class="c-pagination" aria-label="ページ送り">${prev}<div class="c-pagination-nav">${nums}</div>${next}</nav>`;
  }

  // 先頭・末尾・現在の前後を表示し、間は … で省略
  pageNumbers(cur, total) {
    const wanted = new Set([1, total, cur, cur - 1, cur + 1]);
    const list = [...wanted].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    list.forEach((n) => {
      if (n - prev > 1) out.push('...');
      out.push(n);
      prev = n;
    });
    return out;
  }

  bindPager() {
    this.pagerEl.querySelectorAll('[data-page]').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const n = parseInt(a.dataset.page, 10);
        if (!Number.isNaN(n)) this.setPage(n);
      });
    });
  }

  /* ===== URL同期（パラメータ型） ===== */
  syncUrl() {
    const url = new URL(window.location.href);
    const p = url.searchParams;
    // 既存のフィルタ系をクリアして貼り直す
    ['page', 'q'].forEach((k) => p.delete(k));
    this.facetFields.forEach((f) => p.delete(f));

    // facets：各フィールドの値を「フィールド名」キーで列挙（複数値は同名キーの繰り返し ?tags=A&tags=B）
    Object.entries(this.filterState.facets || {}).forEach(([field, values]) => {
      (values || []).forEach((v) => p.append(field, v));
    });
    if (this.enableSearch && this.filterState.keyword) p.set('q', this.filterState.keyword);
    if (this.currentPage > 1) p.set('page', String(this.currentPage));

    window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
  }

  restoreFromUrl() {
    const p = new URL(window.location.href).searchParams;

    const pg = parseInt(p.get('page'), 10);
    this.currentPage = (!Number.isNaN(pg) && pg > 0) ? pg : 1;

    const facets = {};
    this.facetFields.forEach((f) => {
      // 単一/複数を区別せず同名キーで取得（?category=X や ?tags=A&tags=B）
      const vals = p.getAll(f);
      if (vals.length) facets[f] = vals;
    });
    this.filterState = { keyword: this.enableSearch ? (p.get('q') || '') : '', facets };
  }

  // canonical を絞り込み状態に追従（対象＝canonicalFacetsのみ・page/q/他facetは含めない）。
  // 対象facetがURLに有れば self-canonical（base+?facet=値）、無ければ base に戻す
  updateCanonical() {
    if (!this.canonicalLink || !this.baseCanonical || !this.canonicalFacets.length) return;
    const params = new URLSearchParams();
    this.canonicalFacets.forEach((f) => {
      (this.filterState.facets[f] || []).forEach((v) => params.append(f, v));
    });
    const q = params.toString();
    this.canonicalLink.setAttribute('href', q ? `${this.baseCanonical}?${q}` : this.baseCanonical);
  }

  // 見出しを絞り込みに追従（例 data-list-heading="ブログ" → 未選択「すべてのブログ」／選択時「〇〇のブログ」）。
  // カテゴリ・タグどちらの絞り込みでも効くよう、data-facets の順で最初に選択のあるfacetを使う（複数値は「・」で連結）
  updateHeading() {
    if (!this.headingEl) return;
    const label = this.headingEl.dataset.listHeading || '';
    const selected = this.facetFields
      .map((field) => this.filterState.facets[field] || [])
      .find((values) => values.length) || [];
    const name = selected.join('・');
    this.headingEl.textContent = name ? `${name}の${label}` : `すべての${label}`;
  }

  // 初回ロード時に絞り込み/検索/pageのいずれかが付いていたら、ページ頭のピックアップ節を非表示（クリーンなトップURLの時のみ表示）。
  // init で一度だけ呼ぶ＝以降の絞り込み操作では触らない（MTG準拠）
  hidePickupOnFilteredLoad() {
    const hasParam = this.currentPage > 1
      || (this.enableSearch && !!this.filterState.keyword)
      || this.facetFields.some((f) => (this.filterState.facets[f] || []).length > 0);
    if (!hasParam) return;
    const pickup = document.querySelector('[data-pickup]');
    if (pickup) pickup.hidden = true;
  }

  /* ===== ヘルパ ===== */
  formatDate(d) {
    if (!d) return '';
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}.${m[2]}.${m[3]}` : String(d);
  }

  /**
   * category（文字列または配列）を c-badge に変換して返す。
   * MTで複数カテゴリが登録された場合に配列で渡っても全件バッジ出力できるよう対応。
   * @param {string|string[]} category - 文字列（既存）または配列（MT複数カテゴリ対応）
   * @param {string} [mod=''] - 追加クラス（例 'cl-line'）
   * @returns {string} HTMLフラグメント
   */
  categoryBadges(category, mod = '') {
    const cats = Array.isArray(category)
      ? category.filter(Boolean)
      : (category ? [category] : []);
    if (!cats.length) return '';
    const cls = mod ? `c-badge ${mod}` : 'c-badge';
    return cats.map((c) => `<span class="${cls}">${this.escape(c)}</span>`).join('');
  }

  escape(s) {
    const div = document.createElement('div');
    div.textContent = s == null ? '' : String(s);
    return div.innerHTML;
  }
}
